import { queryOptions, useQuery, useQueryClient } from '@tanstack/react-query'
import { useDispatch } from 'react-redux'
import {
  getAPIKeys,
  getAPIUser,
} from 'src/queries/organizations/apikeys.network'
import { apiUsersQueryKeys } from 'src/queries/queries.constants'
import {
  ExtendBaseQueryOptions,
  MutationMethod,
  useExtendDelete,
  useExtendMutation,
} from 'src/queries/queries.utils'
import {
  createSetIsAPIKeyCreatedPopupOpen,
  createSetIsDeleteAPIKeyPopupOpen,
} from 'src/redux/actions/app.actions'

export const formatDateString = (dateString: string): string => {
  const date = new Date(dateString)

  const month = (date.getMonth() + 1).toString().padStart(2, '0')
  const day = date.getDate().toString().padStart(2, '0')
  const year = date.getFullYear().toString().slice(-2)

  return `${month}/${day}/${year}`
}

export enum APIUserStatus {
  ACTIVE = 'ACTIVE',
  PENDING_VERIFICATION = 'PENDING_VERIFICATION',
  PENDING_APPROVAL = 'PENDING_APPROVAL',
  DENIED = 'DENIED',
}

export type APIKeyResponse = {
  id: string
  secretId: string
  expires: string
  createdAt: string
}

export type APIKeyCreatedResponse = {
  apiKeyId: string
  apiSecret: string
  expiresAt: string
}

export type GetAPIKeysSuccessPayload = {
  apiKeys: APIKeyResponse[]
  apiKeyCount: number
  remainingApiKeyQuota: number
}

export type APIUserResponse = {
  apiUserId: string
  isActive: boolean
  status: APIUserStatus
}

export type DeleteAPIKeyResponse = {
  apiKeyId: string
}

interface QueryAPIUserOptions
  extends ExtendBaseQueryOptions<APIUserResponse, APIUserResponse> {}

export const apiUserQueries = {
  me: () =>
    queryOptions({
      queryKey: apiUsersQueryKeys.me(),
      queryFn: () => getAPIUser(),
      retry: false,
    }),
}

export const useQueryAPIUser = ({
  enabled = true,
  queryCacheOptions,
  ...rest
}: QueryAPIUserOptions) => {
  return useQuery({
    ...apiUserQueries.me(),
    enabled: enabled,
    select: (data: APIUserResponse) => data,
    ...queryCacheOptions,
    ...rest,
  })
}

interface QueryAPIKeysOptions
  extends ExtendBaseQueryOptions<
    GetAPIKeysSuccessPayload,
    GetAPIKeysSuccessPayload
  > {}

export const apiKeysQueries = {
  list: () =>
    queryOptions({
      queryKey: apiUsersQueryKeys.apiKeys(),
      queryFn: () => getAPIKeys(),
      retry: false,
    }),
}

export const useQueryAPIKeys = ({
  enabled = true,
  queryCacheOptions,
  ...rest
}: QueryAPIKeysOptions) => {
  return useQuery({
    ...apiKeysQueries.list(),
    enabled: enabled,
    select: (data: GetAPIKeysSuccessPayload) => data,
    ...queryCacheOptions,
    ...rest,
  })
}

export const usePostAPIUser = () => {
  const queryClient = useQueryClient()
  return useExtendMutation<APIUserResponse, { body: object }>(
    MutationMethod.POST,
    {
      mutationUrl: () => `/apikeys/users/`,
      onSuccess: (res) => {
        queryClient.setQueryData(apiUsersQueryKeys.me(), {
          apiUserId: res.apiUserId,
          isActive: false,
        })
      },
    },
  )
}

export const usePostAPIKey = () => {
  const queryClient = useQueryClient()
  const dispatch = useDispatch()
  return useExtendMutation<APIKeyCreatedResponse, { body: object }>(
    MutationMethod.POST,
    {
      mutationUrl: () => `/apikeys/`,
      onSuccess: (res) => {
        queryClient.invalidateQueries({ queryKey: apiUsersQueryKeys.apiKeys() })
        dispatch(
          createSetIsAPIKeyCreatedPopupOpen(true, {
            id: res.apiKeyId,
            secret: res.apiSecret,
            expiry: formatDateString(res.expiresAt),
          }),
        )
      },
    },
  )
}

export const useDeleteAPIKey = () => {
  const queryClient = useQueryClient()
  const dispatch = useDispatch()
  return useExtendDelete<DeleteAPIKeyResponse, { body: { apiKeyId: string } }>({
    mutationUrl: (vars) => `/apikeys/${vars.body.apiKeyId}`,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: apiUsersQueryKeys.apiKeys() })
      dispatch(createSetIsDeleteAPIKeyPopupOpen(false))
    },
  })
}
